import React from 'react';
import {produce, Draft} from 'immer';
import classNames from "classnames";
import {
    AgoraPublisher,
    MEDIA_QUALITY_AUDIO_PROFILES_CODES_MAP,
    MEDIA_QUALITY_VIDEO_PROFILES_CODES_MAP,
    MasterRemoteUsersTypeDef
} from "../../services/AgoraService";
import {TaggedLogger} from "@nextlot/core/utilities";
import AuctionsManager from "@nextlot/core/webcast/services/AuctionsManager";
import {Tooltip} from "../floatui_components";


const _logger = TaggedLogger.get('ClerkMediaStreamingCmp');


const VIDEO_PREVIEW_PLAYER_ELEMENT_ID = 'publisher_container';

const VIDEO_PREVIEW_WIDTH = 270;
const VIDEO_PREVIEW_HEIGHT = VIDEO_PREVIEW_WIDTH * 0.75; // 4:3 ratio


const DEFAULT_STREAM_SETTINGS = {
    audioProfileCode: 'standard',
    videoProfileCode: '640_480_15',
};



type CmpState = Partial<{
    statusLogLines: Array<String>,

    showSettingsPanel:boolean|'wait',
    showStatusLogPanel:boolean,

    connectStatus:number,
    publisherStatus:number,

    streamHasAudio:boolean,
    streamHasVideo:boolean,
    streamProfileCodeForAudio?:string,
    streamProfileCodeForVideo?:string,

    newStreamHasAudio:boolean,
    newStreamHasVideo:boolean,
    newStreamProfileCodeForAudio:string,
    newStreamProfileCodeForVideo:string,

    isPublishActive:boolean,
    muteAudio:boolean,
    previewVideo:boolean,
}>;


enum StatusLogLevelEnum {
    info = 'info',
    warn = 'warn',
    error = 'error',
}
type StatusLogLineTypeDef = [StatusLogLevelEnum, string];


type LocalMediaDevicesTypeDef = {
    audio:MediaDeviceInfo[],
    video:MediaDeviceInfo[]
}

enum PublisherStatusEnum {
    initializing = 0,
    connected_ok = 2,

    local_devices_enabling = 3,
    local_devices_available = 4,

    local_tracks_created = 5,

    publish_ok = 7,

    connect_error = -1,
}


enum PanelVisibleStatesEnum {
    closed,
    visible,
    visible_locked
}

type MediaKind = 'audio'|'video';
type MediaKindStreamingSettings = {
    isEnabled: boolean,
    deviceId?: string,
    profileCode?: string,
}

type StreamingSettingsTypeDef = Record<MediaKind, MediaKindStreamingSettings>;


const PublisherStreamingContext = React.createContext({
    // stage: PublisherStatusEnum.initializing,
    // setPublisherState: ()
});


export default function ClerkMediaStreamingCmp() {
    _logger.debug('.render()');

    const refAgoraPublisher = React.useRef<AgoraPublisher>(null);

    const refPublisherStatusValue = React.useRef<PublisherStatusEnum>(PublisherStatusEnum.initializing);
    const [publisherStatus, setPublisherStatus] = React.useState<PublisherStatusEnum>(PublisherStatusEnum.initializing);

    const [statusLogLines, dispatchStatusLogLines] = React.useReducer(
        produce((draft: Draft<StatusLogLineTypeDef[]>, action: { type:string, payload?: any }) => {
            switch (action.type) {
                case 'update':
                    // append payload to last status line
                    draft[draft.length - 1][1] = draft[draft.length - 1][1] + ' ' + action.payload;
                    break;

                case 'add':
                    draft.push(action.payload);
                    break;
            }
        }),
        [
            [StatusLogLevelEnum.info, "Initializing ... "]
        ]);

    const [statusLogPanelVisibleState, setStatusLogPanelVisibleState] = React.useState(PanelVisibleStatesEnum.visible_locked);
    const [settingsPanelVisibleState, setSettingsPanelVisibleState] = React.useState(PanelVisibleStatesEnum.closed);

    const [localMediaDevices, setLocalMediaDevices] = React.useState<LocalMediaDevicesTypeDef>(null);

    const [streamingSettings, setStreamingSettings] = React.useState<StreamingSettingsTypeDef>({ audio: { isEnabled: false }, video: { isEnabled: false } });

    const [isLocalPublisherAudioMasterStreaming, setIsLocalPublisherAudioMasterStreaming] = React.useState(false);
    const [isLocalPublisherVideoMasterStreaming, setIsLocalPublisherVideoMasterStreaming] = React.useState(false);

    const handleRemoteMasterStreamsChanged = (change:MasterRemoteUsersTypeDef) => {
        _logger.debug('agoraPublisher.onRemoteMasterStreamsChanged: change:', change);

        if (refPublisherStatusValue.current < PublisherStatusEnum.publish_ok) {
            // ignore the event if we don't have a local publisherStream
            return;
        }

        (typeof change.audioRemoteUser === 'object') && setIsLocalPublisherAudioMasterStreaming(false);
        (typeof change.videoRemoteUser === 'object') && setIsLocalPublisherVideoMasterStreaming(false);

    };


    React.useEffect(() => {
        refAgoraPublisher.current = new AgoraPublisher(AuctionsManager.currentWebcastAuctionHandler);
        const agoraPublisher = refAgoraPublisher.current;

        dispatchStatusLogLines({ type: 'update', payload: 'OK'});

        dispatchStatusLogLines({ type: 'add', payload: [StatusLogLevelEnum.info, 'Connecting ...']})

        agoraPublisher.connectAndJoinChannel()
            .then(() => {
                setPublisherStatus(PublisherStatusEnum.connected_ok);

                dispatchStatusLogLines({ type: 'update', payload: `CONNECTED [${agoraPublisher.agoraChannelName}]` });

                setStatusLogPanelVisibleState(PanelVisibleStatesEnum.closed);

                agoraPublisher.onRemoteMasterStreamsChanged(handleRemoteMasterStreamsChanged);
            })
            .catch(() => {
                setPublisherStatus(PublisherStatusEnum.connect_error);
                setStatusLogPanelVisibleState(PanelVisibleStatesEnum.visible_locked);
            })

    }, []);




    const handleClick_enableDevices = () => {

        setPublisherStatus(PublisherStatusEnum.local_devices_enabling);

        dispatchStatusLogLines({ type: 'add', payload: [StatusLogLevelEnum.info, 'Scanning local media devices ...']})

        refAgoraPublisher.current.scanAndLoadLocalMediaDevices().then((publisher) => {
            dispatchStatusLogLines({ type: 'update', payload: 'OK.'});

            setLocalMediaDevices({
                audio: publisher.localMediaDevicesAudio,
                video: publisher.localMediaDevicesVideo,
            });

            setStreamingSettings({
                audio: {
                    deviceId: publisher.localMediaDevicesAudio[0].deviceId,
                    isEnabled: true,
                    profileCode: DEFAULT_STREAM_SETTINGS.audioProfileCode,
                },
                video: {
                    deviceId: publisher.localMediaDevicesVideo[0].deviceId,
                    isEnabled: true,
                    profileCode: DEFAULT_STREAM_SETTINGS.videoProfileCode,
                },
            })

            setPublisherStatus(PublisherStatusEnum.local_devices_available);

            publisher.createLocalTracks().then((publisher) => {
                publisher.localVideoTrack.play(VIDEO_PREVIEW_PLAYER_ELEMENT_ID);

                // open the SettingsPanel only after the local tracks are created
                setSettingsPanelVisibleState(PanelVisibleStatesEnum.visible);
            });
        });

    }


    const handleApplyNewStreamingSettings = async (newStreamingSettings:StreamingSettingsTypeDef) => {
        setSettingsPanelVisibleState(PanelVisibleStatesEnum.visible_locked);

        const agoraPublisher = refAgoraPublisher.current;

        if ((!!newStreamingSettings.video.isEnabled) !== (!!streamingSettings.video.isEnabled)) {
            // use ILocalTrack.setMuted better than .setEnabled
            // see https://docs.agora.io/en/Interactive%20Broadcast/faq/differences_between_setenabled_and_setmuted
            await agoraPublisher.localVideoTrack.setMuted(! newStreamingSettings.video.isEnabled);
        }

        if (newStreamingSettings.video.deviceId !== streamingSettings.video.deviceId) {
            await agoraPublisher.localVideoTrack.setDevice(newStreamingSettings.video.deviceId)
        }

        if (newStreamingSettings.video.profileCode !== streamingSettings.video.profileCode) {
            await agoraPublisher.localVideoTrack.setEncoderConfiguration(MEDIA_QUALITY_VIDEO_PROFILES_CODES_MAP[newStreamingSettings.video.profileCode || DEFAULT_STREAM_SETTINGS.videoProfileCode]);
        }


        if ((!!newStreamingSettings.audio.isEnabled) !== (!!streamingSettings.audio.isEnabled)) {
            // use ILocalTrack.setMuted better than .setEnabled
            // see https://docs.agora.io/en/Interactive%20Broadcast/faq/differences_between_setenabled_and_setmuted
            await agoraPublisher.localAudioTrack.setMuted(! newStreamingSettings.audio.isEnabled);
        }

        if (newStreamingSettings.audio.deviceId !== streamingSettings.audio.deviceId) {
            await agoraPublisher.localAudioTrack.setDevice(newStreamingSettings.audio.deviceId)
        }

        if (newStreamingSettings.audio.profileCode !== streamingSettings.audio.profileCode) {
            // await agoraPublisher.localAudioTrack.set setEncoderConfiguration(MEDIA_QUALITY_AUDIO_PROFILES_CODES_MAP[newStreamingSettings.audio.profileCode || DEFAULT_STREAM_SETTINGS.audioProfileCode]);
        }

        setStreamingSettings(newStreamingSettings);
        setSettingsPanelVisibleState(PanelVisibleStatesEnum.closed);
    }



    const handleClick_Publish = () => {
        refAgoraPublisher.current.publish().then((value) => {
            setPublisherStatus(PublisherStatusEnum.publish_ok);
            setIsLocalPublisherAudioMasterStreaming(streamingSettings.audio.isEnabled);
            setIsLocalPublisherVideoMasterStreaming(streamingSettings.video.isEnabled);
        });
    }


    // Updated with every component re-render and needed to keep the publisherStatus state value up-to-date
    // inside the .onRemoteMasterStreamsChanged event listener instance.

    refPublisherStatusValue.current = publisherStatus;

    // STAGES:
    // 1. connect+join
    // 2. enable media devices (i.e. allow permission)
    // 3. settings

    const isMediaDevicesAvailable = localMediaDevices && (publisherStatus >= PublisherStatusEnum.local_devices_available); // strictly after connected_ok

    return (
        <PublisherStreamingContext.Provider value={{}}>
            <div className='clerk-media-streaming-cmp-wrapper'>
                <div className='media-streaming__body'>
                    {
                        (publisherStatus >= PublisherStatusEnum.local_devices_enabling)
                            ? <>
                                <div id={VIDEO_PREVIEW_PLAYER_ELEMENT_ID} className='publisher-container' />

                                {
                                    (publisherStatus === PublisherStatusEnum.local_devices_enabling)
                                        ? <div className='p-2'>Enabling local media devices ...</div>
                                        : <>
                                            <SettingsPanel
                                                visibleState={settingsPanelVisibleState}
                                                onClose={() => setSettingsPanelVisibleState(PanelVisibleStatesEnum.closed)}
                                                localMediaDevices={localMediaDevices}
                                                publisherStatus={publisherStatus}
                                                streamingSettings={streamingSettings}
                                                onApply={handleApplyNewStreamingSettings}
                                            />
                                        </>
                                }
                            </>
                            : <div className='p-2'>
                                <h4>Setup Media Streaming</h4>
                                <p>Click <button className='btn btn-sm btn-info' onClick={handleClick_enableDevices}>Enable <i className='fa-regular fa-video fa-fw' /> <i className='fa-regular fa-microphone fa-fw' /></button> and if prompted
                                    please &quot;Allow&quot; your browser to access the camera and microphone.</p>
                            </div>
                    }


                    {
                        (statusLogPanelVisibleState !== PanelVisibleStatesEnum.closed)
                        &&
                        <StatusLogPanel visibleState={statusLogPanelVisibleState} onClose={() => setStatusLogPanelVisibleState(PanelVisibleStatesEnum.closed)} lines={statusLogLines}/>
                    }
                </div>

                <div className="p-2 hstack gap-1">

                    <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => setStatusLogPanelVisibleState(PanelVisibleStatesEnum.visible)}>
                        <i className="fa-regular fa-stream"/>
                    </button>

                    {
                        (isMediaDevicesAvailable)
                        &&
                        <>
                                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => setSettingsPanelVisibleState(PanelVisibleStatesEnum.visible)}>
                                    <i className="fa-regular fa-cogs fa-fw"/>
                                    &nbsp;
                                    <i className='fa-regular fa-microphone fa-fw'/>
                                    <i className='fa-regular fa-video fa-fw'/>
                                  </button>
                        </>
                    }

                    <div className="ms-auto vr"></div>

                    {
                        <span className="fa-stack" style={{verticalAlign: 'top'}}>
                          {
                            publisherStatus >= PublisherStatusEnum.publish_ok && streamingSettings.audio.isEnabled // Streaming is enabled
                                ? isLocalPublisherAudioMasterStreaming // I am master streaming audio
                                    ? <>
                                        <i className="fa-regular fa-circle fa-stack-2x text-danger"></i>
                                        <i className='fa-regular fa-microphone fa-fw fa-stack-1x fa-inverse text-danger' />
                                      </>
                                    : <>
                                        <i className="fa-regular fa-circle fa-stack-2x"></i>
                                        <i className='fa-regular fa-microphone fa-fw fa-stack-1x' />
                                      </>
                                : streamingSettings.audio.isEnabled // Streaming is disabled and device is set
                                      ? <>
                                          <i className="fa-regular fa-circle fa-stack-2x fa-inverse" />
                                          <i className='fa-regular fa-microphone fa-stack-1x fa-fw' />
                                        </>
                                      : <>
                                          <i className="fa-regular fa-circle fa-stack-2x fa-inverse" />
                                          <i className='fa-regular fa-microphone-slash fa-stack-1x fa-fw text-body-secondary' />
                                        </>
                          }
                        </span>
                      }


                    {
                        <span className="fa-stack" style={{verticalAlign: 'top'}}>
                          {
                            publisherStatus >= PublisherStatusEnum.publish_ok && streamingSettings.video.isEnabled // Streaming is enabled
                                ? isLocalPublisherVideoMasterStreaming // I am master streaming video
                                    ? <>
                                        <i className="fa-regular fa-circle fa-stack-2x text-danger"></i>
                                        <i className='fa-regular fa-video fa-fw fa-stack-1x fa-inverse text-danger' />
                                      </>
                                    : <>
                                        <i className="fa-regular fa-circle fa-stack-2x"></i>
                                        <i className='fa-regular fa-video fa-fw fa-stack-1x' />
                                      </>
                                : streamingSettings.video.isEnabled // Streaming is disabled and device is set
                                      ? <>
                                          <i className="fa-regular fa-circle fa-stack-2x fa-inverse" />
                                          <i className='fa-regular fa-video fa-stack-1x fa-fw' />
                                        </>
                                      : <>
                                          <i className="fa-regular fa-circle fa-stack-2x fa-inverse" />
                                          <i className='fa-regular fa-video-slash fa-stack-1x fa-fw text-body-secondary' />
                                        </>
                          }
                        </span>
                      }

                    <div className='ms-1'>
                        {
                            (publisherStatus === PublisherStatusEnum.connect_error)
                                ? <span>
                                    <i className='fa-regular fa-fw fa-signal-stream-slash text-danger' />
                                  </span>
                                : (publisherStatus < PublisherStatusEnum.connected_ok)
                                    ? <span>
                                        <i className='fa-regular fa-fw fa-signal-stream text-body-secondary' />
                                      </span>
                                    : <div className='d-flex align-items-center'>
                                        <i className='fa-regular fa-fw fa-signal-stream text-success' />

                                        &nbsp;

                                        {
                                            (publisherStatus >= PublisherStatusEnum.publish_ok && (streamingSettings.audio.isEnabled || streamingSettings.video.isEnabled))
                                            ? <div
                                                style={{letterSpacing: '0.15em'}}
                                                className='btn btn-sm btn-transparent font-monospace fw-bolder text-danger border-0 shadow-none pe-none'
                                              >
                                                LIVE
                                              </div>
                                            : <button type='button' className='btn btn-sm btn-danger' onClick={handleClick_Publish}
                                              disabled={! (streamingSettings.audio.isEnabled || streamingSettings.video.isEnabled)}> GoLive! </button>
                                        }
                                      </div>
                        }
                    </div>


                </div>
            </div>
        </PublisherStreamingContext.Provider>
    );
}



function StatusLogPanel({ lines, visibleState, onClose }:{ lines:StatusLogLineTypeDef[], visibleState:PanelVisibleStatesEnum, onClose:Function }) {

    const refStatusLogBottomLine = React.useRef(null);

    React.useEffect(() => {
        refStatusLogBottomLine.current?.scrollIntoView();
    }, [lines]);

    return (
        <div className='panel-container'>
            <div className='card-header p-1 d-flex flex-row justify-content-between'>
                <div className='text-dark'>Media Streaming Status</div>
                {
                    visibleState !== PanelVisibleStatesEnum.visible_locked
                    &&
                    <button type='button' className='btn btn-sm btn-dark' onClick={() => { onClose() }}>&times;</button>
                }
            </div>
            <div className='card-body p-1'>
                <ul className='list-container'>
                    {lines.map((logLine, idx) => {
                        return (
                            <li key={logLine[0]+logLine[1]} className={`level-${logLine[0]}`}>
                                <b>&rsaquo;&nbsp;</b>
                                <span>{logLine[1]}</span>
                            </li>
                        )
                    })}
                    <li ref={refStatusLogBottomLine}>&nbsp;</li>
                </ul>
            </div>
        </div>
    );
}




type SettingsPanelProps = {
    visibleState:PanelVisibleStatesEnum,
    onClose: ()=>void,
    onApply: (StreamingSettingsTypeDef)=>void,
    localMediaDevices:LocalMediaDevicesTypeDef,

    publisherStatus:PublisherStatusEnum,
    streamingSettings:StreamingSettingsTypeDef,
}


function cloneStreamingSettings(streamingSettings:StreamingSettingsTypeDef):StreamingSettingsTypeDef {
    return {
        audio: { ... streamingSettings?.audio },
        video: { ... streamingSettings?.video },
    }
}

function SettingsPanel(props:SettingsPanelProps) {
    _logger.debug('SettingsPanel. render:  streamingSettings:', props.streamingSettings);

    const { visibleState, onClose, onApply, localMediaDevices, publisherStatus, streamingSettings } = props;

    const isPublishActive:boolean = publisherStatus >= PublisherStatusEnum.publish_ok;


    const [newStreamingSettings, dispatchStreamingSettings] = React.useReducer(
        produce((draft: Draft<StreamingSettingsTypeDef>, action: { type:string, payload?: any }) => {
            switch(action.type) {
                case 'init': {
                    _logger.debug('SettingsPanel.reducer: INIT', action);
                    return cloneStreamingSettings(action.payload);
                }

                case 'update': {
                    draft[action.payload.media][action.payload.prop] = action.payload.value;
                }
            }

        }), streamingSettings, cloneStreamingSettings);

    const dispatchUpdateActionStreamingSettings = (media: 'audio'|'video', prop: keyof MediaKindStreamingSettings, value?: any) => {
        dispatchStreamingSettings({ type: 'update', payload: { media, prop, value } });
    }

    React.useEffect(() => {
        // every time the panel is visible: re-init the settings!
        // i.e. copy the props settings into a local copy, which will be then handled by the local reducer
        if (visibleState !== PanelVisibleStatesEnum.closed) {
            dispatchStreamingSettings({type: 'init', payload: streamingSettings});
        }
    }, [visibleState]);

    let applyButton = null;

    // if (hasAtLeastOneTrackChecked) {
    //     applyButton = <button type='button' onClick={this.handleClick_SettingsPanelApply}
    //                           className={`btn btn-sm ${isPublishActive ? 'btn-danger' : 'btn-info'}`}>Apply</button>;
    // }
    // else if (isPublishActive) {
    //     // non checked, and active publish => unpublish
    //     applyButton = <button type='button' onClick={this.handleClick_SettingsPanelUnpublish}
    //                           className='btn btn-sm btn-danger'>Un-publish</button>;
    //
    // }
    // else {
    // }


    const handleChange_checkboxForMediaKind = (mediaKind:MediaKind) => (evt) => {
        dispatchUpdateActionStreamingSettings(mediaKind, 'isEnabled', evt.target.checked);
    }


    const handleChange_selectForMediaKind = (mediaKind:MediaKind, prop: keyof MediaKindStreamingSettings) => (evt) => {
        dispatchUpdateActionStreamingSettings(mediaKind, prop, evt.target.value);
    }


    return (
        <div className={classNames('panel-container', { 'd-none': visibleState === PanelVisibleStatesEnum.closed })}>
            <div className='card-header'>
                <span>Media Streaming Settings</span>
            </div>
            <div className='card-body p-1'>
                <div className="vstack gap-2">

                    <div className='mb-1'>
                        <label>
                            <input type="checkbox" value={'has_audio'}
                                   checked={!!newStreamingSettings.audio.isEnabled}
                                   onChange={handleChange_checkboxForMediaKind('audio')}
                                // disabled={!this.state.publisherStatus} // if publisher was never initialized, then disable so user can't uncheck this
                            />
                            &nbsp;
                            Audio
                        </label>

                        &nbsp;

                        <select value={newStreamingSettings.audio.profileCode}
                                onChange={handleChange_selectForMediaKind('audio', 'profileCode')}
                                disabled={!newStreamingSettings.audio.isEnabled}
                        >
                            {Object.keys(MEDIA_QUALITY_AUDIO_PROFILES_CODES_MAP).map((profileCode) => {
                                return (<option key={profileCode} value={profileCode}>{profileCode}</option>)
                            })}
                        </select>

                        <label className='ms-3 d-flex flex-row align-items-center'>
                            <i className='fa-regular fa-microphone fa-fw' />&nbsp;
                            <select className='form-select form-select-sm'
                                    value={newStreamingSettings.audio.deviceId}
                                    onChange={handleChange_selectForMediaKind('audio', 'deviceId')}
                                    disabled={!newStreamingSettings.audio.isEnabled}
                            >
                                {localMediaDevices.audio?.map((mdi) => {
                                    return <option key={mdi.deviceId} value={mdi.deviceId}>{mdi.label}</option>
                                })}
                            </select>
                        </label>
                    </div>

                    <div className='mb-1'>
                        <label>
                            <input type="checkbox" value={'has_video'}
                                   checked={!!newStreamingSettings.video.isEnabled}
                                   onChange={handleChange_checkboxForMediaKind('video')}
                                // disabled={!this.state.publisherStatus} // if publisher was never initialized, then disable so user can't uncheck this
                            />
                            &nbsp;
                            Video
                        </label>

                        &nbsp;

                        <select value={newStreamingSettings.video.profileCode}
                                onChange={handleChange_selectForMediaKind('video', 'profileCode')}
                                disabled={!newStreamingSettings.video.isEnabled}
                        >
                            {Object.keys(MEDIA_QUALITY_VIDEO_PROFILES_CODES_MAP).map((profileCode) => {
                                return (<option key={profileCode} value={profileCode}>{profileCode.replace(/(\d+)_(\d+)_(\d+)/, '$1x$2 @ $3 fps')}</option>)
                            })}
                        </select>

                        <label className='ms-3 d-flex flex-row align-items-center'>
                            <i className='fa-regular fa-video fa-fw' />&nbsp;
                            <select className='form-select form-select-sm'
                                    value={newStreamingSettings.video.deviceId}
                                    onChange={handleChange_selectForMediaKind('video', 'deviceId')}
                                    disabled={!newStreamingSettings.video.isEnabled}
                            >
                                {localMediaDevices.video?.map((mdi) => {
                                    return <option key={mdi.deviceId} value={mdi.deviceId}>{mdi.label}</option>
                                })}
                            </select>
                        </label>
                    </div>

                </div>

                {
                    (isPublishActive)
                        ? <div className='alert alert-warning text-wrap small font-weight-light p-1 mb-0'>
                            You are streaming live! Changing settings now will briefly interrupt the streaming.
                        </div>
                        : (1 == 1)
                            ? <span className='badge bg-danger'>Need at least one checked</span>
                            : null
                }

            </div>


            <div className='card-footer'>

                {visibleState === PanelVisibleStatesEnum.visible_locked ?
                    <div>Applying ...</div>
                    :
                    <div className='d-flex justify-content-between'>
                        <button type='button' onClick={() => onApply(cloneStreamingSettings(newStreamingSettings))} className='btn btn-sm btn-primary'>Apply</button>
                        <button type='button' onClick={() => onClose()} className='btn btn-sm btn-outline-secondary'>Cancel</button>
                    </div>
                }

            </div>
        </div>
    )
}




//
// class ClerkMediaStreamingOldCmp extends React.Component<{},CmpState> {
//     private readonly streamingAgoraAppId: string;
//     private readonly streamingAgoraChannelName: string;
//     private readonly streamingAgoraToken: string;
//     private readonly refStatusLogBottomLine: React.RefObject<HTMLLIElement>;
//     private _statusLogLines: Array<any>;
//     private agoraService: AgoraPublisher;
//
//
//     constructor(props) {
//         super(props);
//         _logger.debug('.constructor()', props);
//
//         const {streamingAgoraAppId, streamingAgoraChannelName, streamingAgoraToken} = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData;
//
//         this.streamingAgoraAppId = streamingAgoraAppId;
//         this.streamingAgoraChannelName = streamingAgoraChannelName;
//         this.streamingAgoraToken = streamingAgoraToken;
//
//         this.refStatusLogBottomLine = React.createRef();
//         this._statusLogLines = [];
//
//         this.state = {
//             statusLogLines: [],
//
//             connectStatus: 0,
//             publisherStatus: 0,
//
//             showSettingsPanel: false,
//             showStatusLogPanel: true,
//
//             newStreamHasAudio: true,
//             newStreamHasVideo: true,
//
//             newStreamProfileCodeForAudio: DEFAULT_STREAM_SETTINGS.audioProfileCode,
//             newStreamProfileCodeForVideo: DEFAULT_STREAM_SETTINGS.videoProfileCode,
//
//             streamHasAudio: false,
//             streamHasVideo: false,
//
//             muteAudio:false,
//             previewVideo:true,
//             isPublishActive: false
//         };
//
//
//         this.agoraService = new AgoraPublisher(this.streamingAgoraAppId);
//     }
//
//
//
//     componentDidMount(): void {
//         this.setState({
//             connectStatus: CONNECT_STATUS_CONNECTING
//         });
//
//         this._logStatusDebugMessage(`Channel: ${this.streamingAgoraChannelName}`);
//         this._logStatusDebugMessage(`Connecting ...`);
//
//         this._registerRemoteMasterStreamsChangeListener();
//
//         this.agoraService.connectAndJoinChannel(this.streamingAgoraChannelName, this.streamingAgoraToken)
//             .then(() => {
//                 this.setState({
//                     connectStatus: CONNECT_STATUS_CONNECTED_OK,
//                 });
//
//                 this._logStatusInfoMessage(`Connect SUCCESSFUL.`);
//
//                 setTimeout(() => {
//                     this.setState({
//                         showStatusLogPanel: false,
//                     });
//                 }, 500);
//
//             }, (reason) => {
//                 this.setState({
//                     connectStatus: CONNECT_STATUS_CONNECT_ERROR,
//                 });
//                 this._logStatusErrorMessage(`Connect FAILED: ${reason}`, true);
//             });
//
//         this._scrollStatusLogListToBottom();
//     }
//
//
//
//
//
//     _registerRemoteMasterStreamsChangeListener() {
//         this.agoraService.onRemoteMasterStreamsChanged((change:RemoteMasterStreamsType) => {
//             _logger.debug('_registerRemoteMasterStreamsChangeListener>onRemoteMasterStreamsChanged: change:', change);
//
//             const myStreamUid = this.agoraService.publisherStream?.getId();
//
//             if (! myStreamUid || this.state.publisherStatus < PUBLISHER_STATUS_PUBLISH_OK) {
//                 // ignore the event if we don't have a local publisherStream
//                 _logger.debug('_registerRemoteMasterStreamsChangeListener>onRemoteMasterStreamsChanged: IGNORED! no local publisherStream present or not publishActive');
//                 return;
//             }
//
//             const newHostAudioStreamUid = change.audioStream && change.audioStream.getId();
//             const newHostVideoStreamUid = change.videoStream && change.videoStream.getId();
//
//
//             const newState:CmpState = {
//                 connectStatus: 0,
//                 isPublishActive: false,
//                 muteAudio: false,
//                 newStreamHasAudio: false,
//                 newStreamHasVideo: false,
//                 newStreamProfileCodeForAudio: "",
//                 newStreamProfileCodeForVideo: "",
//                 previewVideo: false,
//                 publisherStatus: 0,
//                 showSettingsPanel: undefined,
//                 showStatusLogPanel: false,
//                 statusLogLines: undefined,
//                 streamHasAudio: false,
//                 streamHasVideo: false
//             };
//             let lostStreamTrackTypes:Array<string> = [];
//
//
//             if (newHostAudioStreamUid && myStreamUid !== newHostAudioStreamUid) {
//                 // we are no longer master for audio
//                 // mute local audio
//                 this.agoraService.publisherStream.muteAudio();
//                 // update new react state
//                 newState.streamHasAudio = false;
//                 newState.newStreamHasAudio = false;
//                 lostStreamTrackTypes.push('audio');
//             }
//
//             if (newHostVideoStreamUid && myStreamUid !== newHostVideoStreamUid) {
//                 // we are no longer master for video
//                 // mute local video
//                 this.agoraService.publisherStream.muteVideo();
//                 // update new react state
//                 newState.streamHasVideo = false;
//                 newState.newStreamHasVideo = false;
//                 lostStreamTrackTypes.push('video');
//             }
//
//
//             if (lostStreamTrackTypes.length) {
//
//                 this._logStatusWarnMessage("Another Clerk is streaming: " + lostStreamTrackTypes.join(' & '));
//
//                 this.setState(newState, () => {
//                     // if both tracks were lost, then unpublish
//                     if (lostStreamTrackTypes.length === 2) {
//                         _logger.debug("_registerRemoteMasterStreamsChangeListener>onRemoteMasterStreamsChanged>setState> lostBothTracks ==> UNPUBLISH");
//                         this.agoraService.unpublish().then(() => {
//                             this.setState({
//                                 publisherStatus: PUBLISHER_STATUS_INIT_OK,
//                             })
//                         });
//                     }
//                 });
//             }
//         });
//     }
//
//
//
//     _createOrUpdatePublisherFromCurrentStateNewSettings():Promise<Stream> {
//         // use the "new*" settings in the current state to create a new stream
//         const audio:string|false = this.state.newStreamHasAudio ? this.state.newStreamProfileCodeForAudio : false;
//         const video:string|false = this.state.newStreamHasVideo ? this.state.newStreamProfileCodeForVideo : false;
//         return this.agoraService.createOrUpdatePublisherStream(audio, video);
//     }
//
//
//     _applyNewSettingsInStateAndPlayStreamPreview(stream:Stream) {
//         this.setState({
//             showSettingsPanel: false, // ensure SettingsPanel is closed
//             streamHasAudio: this.state.newStreamHasAudio,
//             streamHasVideo: this.state.newStreamHasVideo,
//             streamProfileCodeForAudio: this.state.newStreamProfileCodeForAudio,
//             streamProfileCodeForVideo: this.state.newStreamProfileCodeForVideo,
//         }, () => {
//             if (! stream || stream.isPlaying()) {
//                 // ignore when there is no stream or it is already playing
//                 return;
//             }
//
//             stream.play(VIDEO_PREVIEW_PLAYER_ELEMENT_ID, { fit: "contain", muted: true });
//         });
//     }
//
//
//
//     handleClick_ShowStatusLogPanel = () => {
//         this.setState({
//             showStatusLogPanel: true
//         })
//     };
//
//
//
//
//
//     handleClick_EnableCameraAndMic = () => {
//         this._logStatusDebugMessage('Initializing ...');
//
//         this.setState({
//
//             publisherStatus: PUBLISHER_STATUS_INITIALIZING,
//
//         }, () => {
//             this._createOrUpdatePublisherFromCurrentStateNewSettings()
//                 .then((publisherStream:Stream) => {
//
//                     this.setState({
//                         publisherStatus: PUBLISHER_STATUS_INIT_OK,
//                     }, () => {
//
//                         this._applyNewSettingsInStateAndPlayStreamPreview(publisherStream);
//
//                         this._logStatusInfoMessage('Init SUCCESSFUL.');
//                     });
//
//
//                 }, (reason) => {
//                     this.setState({
//                         publisherStatus: PUBLISHER_STATUS_INIT_ERROR,
//                     });
//                     this._logStatusErrorMessage(`Init FAILED: ${reason.msg}`, true);
//                 });
//         });
//     };
//
//
//
//     handleClick_ShowSettingsPanel = () => {
//         this.setState({
//             showSettingsPanel: true
//         })
//     };
//
//     handleChange_checkboxStreamHasAudio = (event) => {
//         this.setState({
//             newStreamHasAudio: event.target.checked
//         });
//     };
//
//     handleChange_checkboxStreamHasVideo = (event) => {
//         this.setState({
//             newStreamHasVideo: event.target.checked
//         });
//     };
//
//
//     handleChange_selectStreamProfileCodeForAudio = (event) => {
//         this.setState({
//             newStreamProfileCodeForAudio: event.target.value
//         })
//     };
//
//     handleChange_selectStreamProfileCodeForVideo = (event) => {
//         this.setState({
//             newStreamProfileCodeForVideo: event.target.value
//         })
//     };
//
//
//
//
//
//     handleClick_SettingsPanelApply = () => {
//         const audioChangedState = this.state.newStreamHasAudio !== this.state.streamHasAudio;
//         const audioChangedProfile = this.state.newStreamHasAudio && (this.state.newStreamProfileCodeForAudio !== this.state.streamProfileCodeForAudio); // only consider profile change if hasVideo
//
//         const videoChangedState = this.state.newStreamHasVideo !== this.state.streamHasVideo;
//         const videoChangedProfile = this.state.newStreamHasVideo && (this.state.newStreamProfileCodeForVideo !== this.state.streamProfileCodeForVideo); // only consider profile change if hasVideo
//
//         if ((audioChangedState || audioChangedProfile) || (videoChangedState || videoChangedProfile)) {
//             // if anything about the settings have changed, then we delegate to the agoraService to update
//
//             _logger.debug('.handleClick_SettingsPanelApply() settings changed: ', this.state);
//
//
//             this.setState({
//                 showSettingsPanel: 'wait',
//             }, () => {
//
//                 this._createOrUpdatePublisherFromCurrentStateNewSettings()
//                     .then((publisherStream) => {
//
//                         this._applyNewSettingsInStateAndPlayStreamPreview(publisherStream);
//
//                     }, (reason) => {
//                         // FAILED createOrUpdatePublisherStream
//                         _logger.error('.handleClick_SettingsPanelApply>createOrUpdatePublisherStream.fail: ', reason);
//                         this._logStatusErrorMessage(`Stream update FAILED: ${reason.msg}`, true);
//                     });
//             });
//
//         }
//         else {
//             // nothing has changed in the settings, just hide the settings panel
//             this.setState({
//                 showSettingsPanel: false,
//             });
//         }
//     };
//
//
//     handleClick_SettingsPanelCancel = () => {
//         // close panel and reset <select> values to original values
//         this.setState({
//             showSettingsPanel: false,
//             newStreamHasAudio: this.state.streamHasAudio,
//             newStreamHasVideo: this.state.streamHasVideo,
//             newStreamProfileCodeForAudio: this.state.streamProfileCodeForAudio,
//             newStreamProfileCodeForVideo: this.state.streamProfileCodeForVideo,
//         });
//     };
//
//
//
//     handleClick_Publish = () => {
//         this.setState({
//             publisherStatus: PUBLISHER_STATUS_PUBLISH_PENDING,
//         }, () => {
//             this.agoraService.publish()
//                 .then(() => {
//                     this.setState({
//                         publisherStatus: PUBLISHER_STATUS_PUBLISH_OK,
//                     })
//                 }, (reason) => {
//                     this.setState({
//                         publisherStatus: PUBLISHER_STATUS_PUBLISH_ERROR,
//                     })
//                 });
//         })
//     };
//
//
//     handleClick_SettingsPanelUnpublish = () => {
//         this.agoraService.unpublish().then(() => {
//             this.setState({
//                 showSettingsPanel: false, // ensure SettingsPanel is closed
//                 streamHasAudio: this.state.newStreamHasAudio,
//                 streamHasVideo: this.state.newStreamHasVideo,
//                 streamProfileCodeForAudio: this.state.newStreamProfileCodeForAudio,
//                 streamProfileCodeForVideo: this.state.newStreamProfileCodeForVideo,
//                 publisherStatus: PUBLISHER_STATUS_INIT_OK,
//             })
//         });
//     };
//
//
//     handleClick_checkboxMuteAudio = () => {
//         const newFlagValue = ! this.state.muteAudio;
//
//         if (this.agoraService.publisherStream) {
//             if (newFlagValue) {
//                 this.agoraService.publisherStream.muteAudio();
//             }
//             else {
//                 this.agoraService.publisherStream.unmuteAudio();
//             }
//         }
//
//         this.setState({
//             muteAudio: newFlagValue,
//         });
//     };
//
//
//     handleClick_checkboxRenderPreviewVideo = () => {
//         const newFlagValue = ! this.state.previewVideo;
//         this.setState({
//             previewVideo: newFlagValue,
//         });
//     };
//
//
//
//
//
//
//     renderControlsWidget() {
//         let settingsButton = null;
//         let streamButtons = null;
//
//
//         if (this.state.connectStatus === CONNECT_STATUS_CONNECTING) {
//             streamButtons = <div>Connecting ...</div>;
//         }
//         else if (this.state.connectStatus === CONNECT_STATUS_CONNECTED_OK) {
//
//             settingsButton = (
//                 <div className='d-flex flex-row justify-content-start'>
//                     <button type='button' className='btn btn-sm btn-outline-secondary'
//                             onClick={this.handleClick_ShowStatusLogPanel}>
//                         <i className="fa-regular fa-stream"/>
//                     </button>
//
//                     &nbsp;
//
//                     {
//                         (this.state.publisherStatus >= PUBLISHER_STATUS_INIT_OK) &&
//
//                         <button type='button' className='btn btn-sm btn-outline-secondary'
//                                 onClick={this.handleClick_ShowSettingsPanel}>
//                 <span>
//                 <i className="fa-regular fa-cogs fa-fw"/>
//                     &nbsp;
//                     <i className='fa-regular fa-microphone fa-fw'/>
//                 <i className='fa-regular fa-video fa-fw'/>
//                 </span>
//                         </button>
//                     }
//
//                 </div>
//             );
//
//
//             if (this.state.publisherStatus >= PUBLISHER_STATUS_INIT_OK) {
//
//                 let liveStatus = null;
//                 if (this.state.publisherStatus === PUBLISHER_STATUS_PUBLISH_OK) {
//                     liveStatus = <span className='text-danger'>&nbsp; LIVE</span>;
//                 }
//                 else if (this.state.publisherStatus === PUBLISHER_STATUS_PUBLISH_PENDING) {
//                     liveStatus = <span>Pending ...</span>
//                 }
//                 else {
//                     liveStatus = <button type='button' className='btn btn-sm btn-danger' onClick={this.handleClick_Publish}
//                                          disabled={! (this.state.streamHasAudio || this.state.streamHasVideo)}>GoLive!</button>
//                 }
//
//
//                 streamButtons = (
//                     <div className={`d-flex flex-row justify-content-end align-items-center ${this.state.publisherStatus === PUBLISHER_STATUS_PUBLISH_OK ? 'text-danger' : 'text-dark'}`}>
//                 <span>
//             {
//                 this.state.streamHasAudio &&
//                 <i className='fa-regular fa-microphone fa-fw'/>
//             }
//                 </span>
//
//                         &nbsp;
//
//                         <span>
//             {
//                 this.state.streamHasVideo &&
//                 <i className='fa-regular fa-video fa-fw'/>
//             }
//
//                 </span>
//
//                         &nbsp;
//
//                         &nbsp;
//
//
//                         {liveStatus}
//                     </div>
//                 )
//             }
//             else {
//                 if (! this.state.publisherStatus) {
//                     // show the button if it was never initialized
//                     streamButtons = (
//                         <button type='button' onClick={this.handleClick_EnableCameraAndMic} className='btn btn-sm btn-info'>
//                             Enable &nbsp; <i className='fa-regular fa-video fa-fw' /> &nbsp; <i className='fa-regular fa-microphone fa-fw' />
//                         </button>
//                     )
//                 }
//             }
//
//         }
//
//
//         return (
//             <React.Fragment>
//                 {settingsButton}
//                 {streamButtons}
//             </React.Fragment>
//         )
//     }
//
//
//
//
//
//
//
//
//     renderStreamQualitySettingsForm() {
//         return (
//             <div className="">
//                 <h6>Stream settings</h6>
//                 <div className='p-1'>
//                     <label>
//                         <input type="checkbox"
//                                value={'has_audio'}
//                                checked={this.state.newStreamHasAudio}
//                                onChange={this.handleChange_checkboxStreamHasAudio}
//                                disabled={!this.state.publisherStatus} // if publisher was never initialized, then disable so user can't uncheck this
//                         /> Audio</label>
//                     &nbsp;
//                     <select value={this.state.newStreamProfileCodeForAudio} onChange={this.handleChange_selectStreamProfileCodeForAudio} disabled={!this.state.newStreamHasAudio}>
//                         {Object.keys(MEDIA_QUALITY_AUDIO_PROFILES_CODES_MAP).map((profileCode) => {
//                             return (<option key={profileCode} value={profileCode}>{profileCode}</option>)
//                         })}
//                     </select>
//                 </div>
//
//                 <div className='p-1'>
//                     <label>
//                         <input type="checkbox"
//                                value={'has_video'}
//                                checked={this.state.newStreamHasVideo}
//                                onChange={this.handleChange_checkboxStreamHasVideo}
//                                disabled={!this.state.publisherStatus} // if publisher was never initialized, then disable so user can't uncheck this
//                         /> Video</label>
//                     &nbsp;
//                     <select value={this.state.newStreamProfileCodeForVideo} onChange={this.handleChange_selectStreamProfileCodeForVideo} disabled={!this.state.newStreamHasVideo}>
//                         {Object.keys(MEDIA_QUALITY_VIDEO_PROFILES_CODES_MAP).map((profileCode) => {
//                             return (<option key={profileCode} value={profileCode}>{profileCode.replace(/(\d+)_(\d+)_(\d+)/, '$1x$2 @ $3 fps')}</option>)
//                         })}
//                     </select>
//                 </div>
//
//             </div>
//         )
//     }
//
//
//
//     renderSettingsPanel() {
//         if ( ! this.state.showSettingsPanel) {
//             return null
//         }
//
//         const isPublishActive:boolean = this.state.publisherStatus >= PUBLISHER_STATUS_PUBLISH_OK;
//         const hasAtLeastOneTrackChecked:boolean = (this.state.newStreamHasAudio || this.state.newStreamHasVideo);
//         let applyButton = null;
//
//         if (hasAtLeastOneTrackChecked) {
//             applyButton = <button type='button' onClick={this.handleClick_SettingsPanelApply}
//                                   className={`btn btn-sm ${isPublishActive ? 'btn-danger' : 'btn-info'}`}>Apply</button>;
//         }
//         else if (isPublishActive) {
//             // non checked, and active publish => unpublish
//             applyButton = <button type='button' onClick={this.handleClick_SettingsPanelUnpublish}
//                                   className='btn btn-sm btn-danger'>Un-publish</button>;
//
//         }
//         else {
//             applyButton = <span className='badge bg-danger'>Need at least one checked</span>
//         }
//
//
//         return (
//             <div className='settings-panel-container card'>
//                 <div className='card-header bg-light'>Media Streaming Settings</div>
//                 <div className='card-body p-1'>
//
//                     {this.renderStreamQualitySettingsForm()}
//
//                     {
//                         (isPublishActive) &&
//                         <div className='alert alert-warning text-wrap small font-weight-light p-1 mb-0'>
//                             Streaming is active! Changing settings now will briefly interrupt the streaming.
//                         </div>
//                     }
//
//                 </div>
//
//
//                 <div className='card-footer'>
//
//                     {this.state.showSettingsPanel === 'wait' ?
//                         <div>Applying ...</div>
//                         :
//                         <div className='d-flex justify-content-between'>
//                             {applyButton}
//                             <button type='button' onClick={this.handleClick_SettingsPanelCancel} className='btn btn-sm btn-outline-secondary'>Cancel</button>
//                         </div>
//                     }
//
//                 </div>
//             </div>
//         )
//     }
//
//
//
//
//
//
//     renderStatusLogPanel() {
//         if ( ! this.state.showStatusLogPanel) {
//             return null
//         }
//
//         return (
//             <div className='status-panel-container card'>
//                 <div className='card-header p-1 d-flex flex-row justify-content-between bg-light'>
//                     <h4 className='m-0'>Media Streaming Status</h4>
//                     <button type='button' className='btn btn-sm btn-dark' onClick={() => this.setState({ showStatusLogPanel: false })}>&times;</button>
//                 </div>
//                 <div className='card-body p-1 position-relative'>
//                     <ul className='list-container'>
//                         {this.state.statusLogLines.map((logLine, idx) => {
//                             return (
//                                 <li key={idx} className={`level-${logLine[0]}`}>
//                                     <b>&rsaquo;&nbsp;</b>
//                                     <span>{logLine[1]}</span>
//                                 </li>
//                             )
//                         })}
//                         <li ref={this.refStatusLogBottomLine}>&nbsp;</li>
//                     </ul>
//                 </div>
//             </div>
//         );
//     }
//
//
//
//
//
//     render() {
//         _logger.debug('.render() state: ', this.state);
//
//         return (
//             <div className='clerk-media-streaming-cmp-wrapper'>
//                 <div className='media-streaming__body'>
//                     {
//                         (this.state.publisherStatus < PUBLISHER_STATUS_INIT_OK) ?
//                             <div className='py-2 px-2'>
//                                 <h4>Setup Media Streaming</h4>
//                                 <p>Click <span className='badge bg-info'>Enable <i className='fa-regular fa-video fa-fw' /> <i className='fa-regular fa-microphone fa-fw' /></span> and if prompted
//                                     please &quot;Allow&quot; your browser to access the camera and microphone.</p>
//                             </div>
//
//                             :
//
//                             <div id={VIDEO_PREVIEW_PLAYER_ELEMENT_ID} className='publisher-container' />
//                     }
//                     {this.renderSettingsPanel()}
//                     {this.renderStatusLogPanel()}
//                 </div>
//
//                 <div className="media-streaming__footer">
//                     {this.renderControlsWidget()}
//                 </div>
//             </div>
//         );
//     }
//
//
// }
